import { supabase } from '../lib/supabase';

type StatementEmailOptions = {
  customMessage?: string;
  filename: string;
};

/** Sends statement PDFs through the authenticated Supabase Edge Function. */
export class EmailService {
  async sendStatementEmail(
    accountId: string,
    pdf: Blob,
    options: StatementEmailOptions,
  ): Promise<{ success: true; to: string }> {
    if (pdf.type && pdf.type !== 'application/pdf') {
      throw new Error('The statement attachment must be a PDF');
    }

    const pdfBase64 = await this.blobToBase64(pdf);
    const { data, error } = await supabase.functions.invoke('send-statement-email', {
      body: { accountId, pdfBase64, filename: options.filename, customMessage: options.customMessage },
    });

    if (error) {
      // Supabase wraps non-2xx Edge Function responses. Read its JSON body so
      // the user sees the actionable server message rather than a generic error.
      const response = (error as unknown as { context?: Response }).context;
      let message = 'The statement email could not be sent. Please try again.';
      if (response) {
        try {
          const body = await response.json() as { error?: string };
          if (body.error) message = body.error;
        } catch {
          // Leave the safe fallback message in place for non-JSON responses.
        }
      }
      throw new Error(message);
    }
    if (!data?.success || !data?.to) throw new Error(data?.error || 'The statement email could not be sent.');

    return { success: true, to: data.to };
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Could not prepare the statement attachment'));
          return;
        }
        resolve(result.split(',')[1] || '');
      };
      reader.onerror = () => reject(new Error('Could not read the statement attachment'));
      reader.readAsDataURL(blob);
    });
  }
}

export default EmailService;
